import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../api';

const MESES = [
  '',
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre'
];

const DIAS_SEMANA = [
  { v: 0, label: 'Domingo' },
  { v: 1, label: 'Lunes' },
  { v: 2, label: 'Martes' },
  { v: 3, label: 'Miércoles' },
  { v: 4, label: 'Jueves' },
  { v: 5, label: 'Viernes' },
  { v: 6, label: 'Sábado' }
];

const CHECKLIST_VALIDACION = [
  'Dataset piloto (5–10 empresas) revisado contra propuesta F29 del SII',
  'Códigos 504 / 562 / 142 / 89 conciliados o documentada la diferencia',
  'Remanente y UTM validados con contador responsable',
  'Correo de prueba recibido (logo, PDF, Reply-To contador IVA)',
  'Ningún AUTO_SEND activo hasta cierre formal del checklist'
];

export default function InformeIvaAdministracionPage() {
  const now = new Date();
  const [mes, setMes] = useState(now.getMonth() + 1);
  const [anio, setAnio] = useState(now.getFullYear());
  const [cfg, setCfg] = useState(null);
  const [meta, setMeta] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [audit, setAudit] = useState([]);
  const [digest, setDigest] = useState(null);
  const [auditFilter, setAuditFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [checklist, setChecklist] = useState(() => {
    try {
      const raw = localStorage.getItem('informeIvaChecklistValidacion');
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  const loadCfg = useCallback(async () => {
    const { data } = await api.get('/informes-iva/config/global');
    setCfg(data);
  }, []);

  const loadMeta = useCallback(async () => {
    const { data } = await api.get('/informes-iva/administracion/meta');
    setMeta(data);
  }, []);

  const loadResumen = useCallback(async () => {
    const { data } = await api.get('/informes-iva/administracion/resumen', { params: { mes, anio } });
    setResumen(data);
  }, [mes, anio]);

  const loadAudit = useCallback(async () => {
    const { data } = await api.get('/informes-iva/audit', { params: { limit: 120 } });
    setAudit(data || []);
  }, []);

  useEffect(() => {
    loadCfg().catch((e) => toast.error(e.response?.data?.error || e.message));
    loadMeta().catch((e) => toast.error(e.response?.data?.error || e.message));
    loadAudit().catch((e) => toast.error(e.response?.data?.error || e.message));
  }, [loadCfg, loadMeta, loadAudit]);

  useEffect(() => {
    loadResumen().catch(() => setResumen(null));
  }, [loadResumen]);

  const toggleChecklist = (item) => {
    setChecklist((prev) => {
      const next = { ...prev, [item]: !prev[item] };
      localStorage.setItem('informeIvaChecklistValidacion', JSON.stringify(next));
      return next;
    });
  };

  const saveCfg = async () => {
    if (!cfg) return;
    if (cfg.autoSendGlobalEnabled && !window.confirm('¿Activar AUTO_SEND global? Solo tras validación tributaria completa.')) {
      return;
    }
    if (cfg.modoOperacion === 'PRODUCCION' && !window.confirm('¿Pasar a PRODUCCIÓN? Se permitirán envíos reales a clientes.')) {
      return;
    }
    if (cfg.digestPorContadorEnabled && cfg.modoOperacion === 'PRODUCCION' && !window.confirm('¿Digest a contadores/oficina en producción?')) {
      return;
    }
    setBusy(true);
    try {
      await api.put('/informes-iva/config/global', {
        modoOperacion: cfg.modoOperacion || 'PRUEBA',
        digestMode: cfg.digestMode,
        digestPorContadorEnabled: cfg.digestPorContadorEnabled,
        digestDestinoPruebaEmail: cfg.digestDestinoPruebaEmail,
        digestScheduleEnabled: cfg.digestScheduleEnabled,
        digestCronDayOfWeek: cfg.digestCronDayOfWeek,
        digestCronHour: cfg.digestCronHour,
        digestCronMinute: cfg.digestCronMinute,
        scheduledCronHour: cfg.scheduledCronHour,
        scheduledCronMinute: cfg.scheduledCronMinute,
        scheduledMaxEjecucionesMes: cfg.scheduledMaxEjecucionesMes,
        periodoIvaDiaCierre: cfg.periodoIvaDiaCierre,
        persistInformeFiles: cfg.persistInformeFiles,
        emailOficina: cfg.emailOficina,
        frescuraHoras: cfg.frescuraHoras,
        timezone: cfg.timezone,
        retencionDias: cfg.retencionDias,
        autoSyncOnRun: cfg.autoSyncOnRun,
        syncF29OnRun: cfg.syncF29OnRun,
        scheduledRunsEnabled: cfg.scheduledRunsEnabled,
        autoSendGlobalEnabled: cfg.autoSendGlobalEnabled,
        permitirRemanenteCeroAsumido: cfg.permitirRemanenteCeroAsumido
      });
      toast.success('Configuración guardada');
      await loadCfg();
      await loadResumen();
      await loadMeta();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const cargarDigest = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/informes-iva/digest/${anio}/${mes}`);
      setDigest(data);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const enviarDigest = async () => {
    const modo = cfg?.digestPorContadorEnabled
      ? 'producción (contadores/oficina según modo)'
      : 'prueba (solo destino abajo)';
    if (!window.confirm(`¿Enviar digest ahora? Modo: ${modo}`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/informes-iva/digest/send/${anio}/${mes}`);
      toast.success(`Digest enviado (modo: ${data.mode || '—'}, OK: ${data.sent ?? 0})`);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const ejecutarCron = async () => {
    if (!window.confirm('¿Ejecutar cron de cálculos programados? No envía correos a clientes salvo AUTO_SEND explícito.')) return;
    setBusy(true);
    try {
      const { data } = await api.post('/informes-iva/jobs/scheduled-run');
      toast.success(`Cron: ${data.enqueued ?? data.processed ?? 0} informe(s) encolados`);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const auditFiltrada = audit.filter((row) => {
    if (!auditFilter.trim()) return true;
    const q = auditFilter.toLowerCase();
    return `${row.action} ${row.entityType} ${row.meta ? JSON.stringify(row.meta) : ''}`.toLowerCase().includes(q);
  });

  if (!cfg) {
    return <div className="max-w-4xl mx-auto p-8 text-slate-500">Cargando administración…</div>;
  }

  const modoPrueba = (cfg.modoOperacion || 'PRUEBA') !== 'PRODUCCION';
  const digestEnPrueba = modoPrueba || !cfg.digestPorContadorEnabled;

  return (
    <div className="max-w-4xl mx-auto px-4 py-10 space-y-10">
      <div>
        <p className="text-sm text-slate-500">
          <Link to="/informes-iva" className="text-indigo-600 hover:underline">
            ← Panel del mes
          </Link>
        </p>
        <h1 className="text-2xl font-bold text-slate-900 mt-1">Administración Informes IVA</h1>
        <p className="text-sm text-slate-600 mt-2">
          Solo super administrador. Aquí controla digest, automatización, crons y piloto. SMTP y plantillas en{' '}
          <Link to="/informes-iva/correo" className="text-indigo-600 underline">
            Correo y envíos
          </Link>
          ; acceso al menú en{' '}
          <Link to="/usuarios" className="text-indigo-600 underline">
            Usuarios
          </Link>
          .
        </p>
      </div>

      <section
        className={`rounded-xl border-2 p-5 ${modoPrueba ? 'bg-amber-50 border-amber-400' : 'bg-emerald-50 border-emerald-500'}`}
      >
        <p className={`text-lg font-bold ${modoPrueba ? 'text-amber-950' : 'text-emerald-950'}`}>
          {modoPrueba ? '🧪 MODO PRUEBA' : '✅ MODO PRODUCCIÓN'}
        </p>
        <p className="text-sm mt-2 text-slate-800">
          {modoPrueba
            ? 'No se envían correos a clientes. Digest solo al destino de prueba. Ideal para piloto.'
            : 'Envíos a clientes permitidos. Digest según configuración de contadores/oficina.'}
        </p>
        <label className="block mt-4 max-w-xs text-sm font-medium">
          Ambiente del módulo
          <select
            className="w-full border rounded-lg px-3 py-2 mt-1 bg-white"
            value={cfg.modoOperacion || 'PRUEBA'}
            onChange={(e) => setCfg({ ...cfg, modoOperacion: e.target.value })}
          >
            <option value="PRUEBA">PRUEBA (piloto)</option>
            <option value="PRODUCCION">PRODUCCIÓN</option>
          </select>
        </label>
      </section>

      {meta && (
        <section className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-xl p-4">
          <p>
            Super admins: <strong>{(meta.superAdminEmails || []).join(', ') || '—'}</strong>
          </p>
          <p className="mt-1">
            Zona horaria: <strong>{cfg.timezone || meta.timezone || 'America/Santiago'}</strong>
          </p>
        </section>
      )}

      {resumen && (
        <section className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
          <div className="border rounded-xl p-3 bg-white">
            <p className="text-slate-500 text-xs">Informes enviados (mes)</p>
            <p className="text-xl font-semibold">{resumen.informesEnviadosMes}</p>
          </div>
          <div className="border rounded-xl p-3 bg-white">
            <p className="text-slate-500 text-xs">Entregas fallidas (mes)</p>
            <p className="text-xl font-semibold text-red-700">{resumen.entregasFallidasMes}</p>
          </div>
          <div className="border rounded-xl p-3 bg-white">
            <p className="text-slate-500 text-xs">Pend. aprobación</p>
            <p className="text-xl font-semibold">{resumen.pendientesAprobacionMes}</p>
          </div>
          <div className="border rounded-xl p-3 bg-amber-50 border-amber-200 col-span-2 sm:col-span-3">
            <p className="text-xs text-amber-900">
              Digest cron:{' '}
              <strong>{resumen.digestCronEnviaAContadores ? 'contadores / oficina' : 'solo prueba'}</strong>
              {!resumen.digestCronEnviaAContadores && (
                <>
                  {' '}
                  → <strong>{resumen.digestPruebaTo || 'super admin'}</strong>
                </>
              )}
              {' · '}
              AUTO_SEND global: <strong>{resumen.autoSendGlobalEnabled ? 'ON' : 'OFF'}</strong> · Clientes auto-envío:{' '}
              <strong>{resumen.clientesConAutoSend}</strong> · Cron cálculos:{' '}
              <strong>{resumen.scheduledRunsEnabled ? 'ON' : 'OFF'}</strong>
            </p>
          </div>
        </section>
      )}

      <section className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4 text-sm">
        <h2 className="font-semibold text-slate-900">Programación digest (correo resumen)</h2>
        <p className="text-xs text-slate-500">
          El servidor revisa cada minuto; no hace falta reiniciar al cambiar horarios (guarde abajo).
        </p>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={cfg.digestScheduleEnabled !== false}
            onChange={(e) => setCfg({ ...cfg, digestScheduleEnabled: e.target.checked })}
          />
          Cron digest activo
        </label>
        <div className="grid sm:grid-cols-3 gap-3 max-w-lg">
          <label className="block text-xs">
            Día semana
            <select
              className="w-full border rounded-lg px-2 py-2 mt-1"
              value={Number(cfg.digestCronDayOfWeek ?? 1)}
              onChange={(e) => setCfg({ ...cfg, digestCronDayOfWeek: Number(e.target.value) })}
            >
              {DIAS_SEMANA.map((d) => (
                <option key={d.v} value={d.v}>
                  {d.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs">
            Hora (0–23)
            <input
              type="number"
              min={0}
              max={23}
              className="w-full border rounded-lg px-2 py-2 mt-1"
              value={cfg.digestCronHour ?? 8}
              onChange={(e) => setCfg({ ...cfg, digestCronHour: Number(e.target.value) })}
            />
          </label>
          <label className="block text-xs">
            Minuto
            <input
              type="number"
              min={0}
              max={59}
              className="w-full border rounded-lg px-2 py-2 mt-1"
              value={cfg.digestCronMinute ?? 0}
              onChange={(e) => setCfg({ ...cfg, digestCronMinute: Number(e.target.value) })}
            />
          </label>
        </div>
        <h2 className="font-semibold text-slate-900 pt-2">Destinatarios digest</h2>
        <p className="text-slate-600 text-xs leading-relaxed">
          {digestEnPrueba ? (
            <>
              Modo <strong>prueba</strong>: un correo con asunto <code className="bg-slate-100 px-1 rounded">[Prueba]</code> a{' '}
              <strong>{resumen?.digestPruebaTo || 'super administrador'}</strong>. Los contadores no reciben nada hasta que active la
              casilla de producción abajo.
            </>
          ) : (
            <>
              Modo <strong>producción</strong>: el cron envía según «Modo digest» (contadores IVA por email de usuario, oficina o ambos).
            </>
          )}
        </p>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={Boolean(cfg.digestPorContadorEnabled)}
            onChange={(e) => setCfg({ ...cfg, digestPorContadorEnabled: e.target.checked })}
          />
          En PRODUCCIÓN: enviar digest a contadores / oficina (si PRUEBA, siempre solo email de prueba)
        </label>
        <label className="block max-w-md">
          Email destino prueba (si modo prueba; vacío = primer super admin)
          <input
            type="email"
            className="w-full border rounded-lg px-3 py-2 mt-1"
            value={cfg.digestDestinoPruebaEmail || ''}
            onChange={(e) => setCfg({ ...cfg, digestDestinoPruebaEmail: e.target.value })}
            placeholder="hans@ormazabalhermanas.cl"
          />
        </label>
        {cfg.digestPorContadorEnabled && (
          <>
            <label className="block max-w-md">
              Modo digest producción
              <select
                className="w-full border rounded-lg px-3 py-2 mt-1"
                value={cfg.digestMode || 'POR_ASIGNADO'}
                onChange={(e) => setCfg({ ...cfg, digestMode: e.target.value })}
              >
                <option value="POR_ASIGNADO">Por contador IVA (email del usuario)</option>
                <option value="GLOBAL_OFICINA">Un correo global oficina</option>
                <option value="AMBOS">Ambos</option>
              </select>
            </label>
            <label className="block max-w-md">
              Email oficina (GLOBAL / AMBOS)
              <input
                type="email"
                className="w-full border rounded-lg px-3 py-2 mt-1"
                value={cfg.emailOficina || ''}
                onChange={(e) => setCfg({ ...cfg, emailOficina: e.target.value })}
              />
            </label>
          </>
        )}
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={busy} onClick={enviarDigest} className="px-4 py-2 border rounded-lg">
            Enviar digest ahora
          </button>
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4 text-sm">
        <h2 className="font-semibold text-slate-900">Cron cálculos (informes automáticos)</h2>
        <p className="text-xs text-slate-500">
          Cada cliente usa su «frecuencia» en Configuración rápida (cada 5/7/15 días, semanal, etc.). Aquí define cuándo corre el
          chequeo diario y reglas globales.
        </p>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={Boolean(cfg.scheduledRunsEnabled)}
            onChange={(e) => setCfg({ ...cfg, scheduledRunsEnabled: e.target.checked })}
          />
          Cron de cálculos activo
        </label>
        <div className="grid sm:grid-cols-2 gap-3 max-w-md">
          <label className="block text-xs">
            Hora chequeo diario (0–23)
            <input
              type="number"
              min={0}
              max={23}
              className="w-full border rounded-lg px-2 py-2 mt-1"
              value={cfg.scheduledCronHour ?? 6}
              onChange={(e) => setCfg({ ...cfg, scheduledCronHour: Number(e.target.value) })}
            />
          </label>
          <label className="block text-xs">
            Minuto
            <input
              type="number"
              min={0}
              max={59}
              className="w-full border rounded-lg px-2 py-2 mt-1"
              value={cfg.scheduledCronMinute ?? 0}
              onChange={(e) => setCfg({ ...cfg, scheduledCronMinute: Number(e.target.value) })}
            />
          </label>
        </div>
        <label className="block max-w-xs text-xs">
          Máx. corridas automáticas por cliente y mes tributario
          <input
            type="number"
            min={1}
            max={31}
            className="w-full border rounded-lg px-2 py-2 mt-1"
            value={cfg.scheduledMaxEjecucionesMes ?? 8}
            onChange={(e) => setCfg({ ...cfg, scheduledMaxEjecucionesMes: Number(e.target.value) })}
          />
        </label>
        <label className="block max-w-md text-xs">
          Día de cierre mes IVA (Chile)
          <input
            type="number"
            min={1}
            max={28}
            className="w-full border rounded-lg px-2 py-2 mt-1"
            value={cfg.periodoIvaDiaCierre ?? 12}
            onChange={(e) => setCfg({ ...cfg, periodoIvaDiaCierre: Number(e.target.value) })}
          />
          <span className="text-slate-500 block mt-1">
            Antes de este día del mes, los crons apuntan al mes tributario anterior; desde este día, al mes en curso.
          </span>
        </label>
        <h2 className="font-semibold text-slate-900 pt-2">Motor y almacenamiento</h2>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={Boolean(cfg.persistInformeFiles)}
            onChange={(e) => setCfg({ ...cfg, persistInformeFiles: e.target.checked })}
          />
          Guardar HTML/PDF en disco del VPS (off = solo BD, PDF al descargar)
        </label>
        <label className="flex items-center gap-2 text-red-900">
          <input
            type="checkbox"
            checked={Boolean(cfg.autoSendGlobalEnabled)}
            onChange={(e) => setCfg({ ...cfg, autoSendGlobalEnabled: e.target.checked })}
          />
          AUTO_SEND global (envío automático al cliente con confianza alta)
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={Boolean(cfg.autoSyncOnRun)}
            onChange={(e) => setCfg({ ...cfg, autoSyncOnRun: e.target.checked })}
          />
          Sync SII al pulsar Informe IVA
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={cfg.syncF29OnRun !== false}
            onChange={(e) => setCfg({ ...cfg, syncF29OnRun: e.target.checked })}
          />
          Incluir formulario F29 en sync SII
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={Boolean(cfg.permitirRemanenteCeroAsumido)}
            onChange={(e) => setCfg({ ...cfg, permitirRemanenteCeroAsumido: e.target.checked })}
          />
          Permitir remanente cero asumido (excepcional)
        </label>
        <div className="grid sm:grid-cols-3 gap-3">
          <label className="block">
            Frescura datos (horas)
            <input
              type="number"
              className="w-full border rounded-lg px-3 py-2 mt-1"
              value={cfg.frescuraHoras ?? 48}
              onChange={(e) => setCfg({ ...cfg, frescuraHoras: Number(e.target.value) })}
            />
          </label>
          <label className="block">
            Retención snapshots (días)
            <input
              type="number"
              className="w-full border rounded-lg px-3 py-2 mt-1"
              value={cfg.retencionDias ?? 365}
              onChange={(e) => setCfg({ ...cfg, retencionDias: Number(e.target.value) })}
            />
          </label>
          <label className="block">
            Zona horaria
            <input
              type="text"
              className="w-full border rounded-lg px-3 py-2 mt-1"
              value={cfg.timezone || 'America/Santiago'}
              onChange={(e) => setCfg({ ...cfg, timezone: e.target.value })}
            />
          </label>
        </div>
        <div className="flex flex-wrap gap-2 pt-2">
          <button type="button" disabled={busy} onClick={saveCfg} className="px-4 py-2 bg-slate-900 text-white rounded-lg disabled:opacity-50">
            Guardar configuración
          </button>
          <button type="button" disabled={busy} onClick={ejecutarCron} className="px-4 py-2 border border-slate-300 rounded-lg disabled:opacity-50">
            Ejecutar cron cálculos (manual)
          </button>
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-3 text-sm">
        <h2 className="font-semibold text-slate-900">Checklist validación tributaria (piloto)</h2>
        <p className="text-xs text-slate-500">Marcadores locales en este navegador.</p>
        <ul className="space-y-2">
          {CHECKLIST_VALIDACION.map((item) => (
            <label key={item} className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={Boolean(checklist[item])} onChange={() => toggleChecklist(item)} className="mt-0.5" />
              <span className={checklist[item] ? 'text-slate-500 line-through' : 'text-slate-800'}>{item}</span>
            </label>
          ))}
        </ul>
      </section>

      <section className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4 text-sm">
        <h2 className="font-semibold text-slate-900">Vista digest (JSON)</h2>
        <div className="flex flex-wrap gap-3 items-end">
          <label>
            Mes
            <select className="block border rounded-lg px-3 py-2 mt-1" value={mes} onChange={(e) => setMes(Number(e.target.value))}>
              {MESES.map((m, i) =>
                i === 0 ? null : (
                  <option key={i} value={i}>
                    {m}
                  </option>
                )
              )}
            </select>
          </label>
          <label>
            Año
            <input type="number" className="block border rounded-lg px-3 py-2 mt-1 w-24" value={anio} onChange={(e) => setAnio(Number(e.target.value))} />
          </label>
          <button type="button" disabled={busy} onClick={cargarDigest} className="px-4 py-2 border rounded-lg">
            Generar vista
          </button>
        </div>
        {digest && (
          <div className="border rounded-lg p-4 bg-slate-50 max-h-96 overflow-auto text-xs font-mono whitespace-pre-wrap">
            {JSON.stringify(digest.resumen ?? digest, null, 2)}
          </div>
        )}
      </section>

      <section className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4 text-sm">
        <div className="flex flex-wrap justify-between gap-3 items-center">
          <h2 className="font-semibold text-slate-900">Auditoría</h2>
          <Link to="/informes-iva/operaciones" className="text-indigo-600 underline text-xs">
            Vista auditoría (todos con permiso configure)
          </Link>
          <button type="button" onClick={loadAudit} className="text-indigo-600 underline text-xs">
            Actualizar
          </button>
        </div>
        <input
          type="search"
          placeholder="Filtrar por acción, entidad…"
          className="w-full border rounded-lg px-3 py-2"
          value={auditFilter}
          onChange={(e) => setAuditFilter(e.target.value)}
        />
        <div className="border rounded-lg overflow-hidden max-h-[420px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-100 sticky top-0">
              <tr>
                <th className="text-left p-2">Fecha</th>
                <th className="text-left p-2">Acción</th>
                <th className="text-left p-2">Detalle</th>
              </tr>
            </thead>
            <tbody>
              {auditFiltrada.map((row) => (
                <tr key={row._id} className="border-t border-slate-100">
                  <td className="p-2 whitespace-nowrap text-slate-500">{new Date(row.createdAt).toLocaleString('es-CL')}</td>
                  <td className="p-2 font-medium">{row.action}</td>
                  <td className="p-2 text-slate-600 break-all">
                    {row.entityType}
                    {row.meta ? ` · ${JSON.stringify(row.meta)}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {auditFiltrada.length === 0 && <p className="p-4 text-slate-500 text-center">Sin registros</p>}
        </div>
      </section>
    </div>
  );
}
